/**
 * FunnelAssist Brief Modal - Collect information for AI funnel generation
 * 
 * Collects:
 * - Funnel objective
 * - Business type / vertical
 * - Target audience
 * - Traffic source (optional but recommended)
 * - Include booking step (yes/no)
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Sparkles, 
  Loader2, 
  Calendar,
  AlertTriangle,
  Target,
  Users,
  Building2,
  Megaphone,
  Check,
  RefreshCw,
  Info
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "@/hooks/use-toast";

export default function AIFunnelBriefModal({
  open,
  onClose,
  onGenerated,
  onCreated, // Alias for onGenerated - called with funnel_id
  funnelName: externalFunnelName,
  funnelSlug: externalFunnelSlug
}) {
  const [step, setStep] = useState(1); // 1 = name/slug, 2 = brief, 3 = generating
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState([]);
  
  // MA-109: UX State System
  const [uiState, setUiState] = useState("idle"); // idle | generating_initial | retrying_once | success | failed_non_retryable | failed_retryable
  const [errorGuidance, setErrorGuidance] = useState("");
  const [attemptCount, setAttemptCount] = useState(0);
  
  // MA-110: Staged progress perception (Final Verdict Refinements)
  // MA-111: Initial value is first sub-step (anchored structure)
  const [progressMessage, setProgressMessage] = useState("Structuring your flow");
  
  // MA-110: Cleanup refs
  const progressIntervalRef = useRef(null);
  const retryDelayRef = useRef(null);
  const successHoldRef = useRef(null);
  
  // Funnel name/slug state - use external values if provided
  const [funnelName, setFunnelName] = useState(externalFunnelName || "");
  const [funnelSlug, setFunnelSlug] = useState(externalFunnelSlug || "");
  
  const [brief, setBrief] = useState({
    purpose: "",
    business_type: "",
    target_audience: "",
    traffic_source: "",
    include_booking: true,
    tone: "friendly"
  });
  
  // MA-GENERATION-LOADER-UX-REFINEMENT-146: Removed dynamic subtext cycling
  // This was redundant with the checklist progress below
  // Keeping the state variables for backward compatibility (unused)
  
  // MA-110: Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      if (retryDelayRef.current) clearTimeout(retryDelayRef.current);
      if (successHoldRef.current) clearTimeout(successHoldRef.current);
    };
  }, []);
  
  const handleNameChange = (e) => {
    const name = e.target.value;
    setFunnelName(name);
    setFunnelSlug(name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  };
  
  const handleContinueToBrief = () => {
    if (!funnelName || !funnelSlug) {
      toast({
        title: "Missing information",
        description: "Please enter funnel name and slug",
        variant: "destructive"
      });
      return;
    }
    setStep(2);
  };
  
  const handleGenerate = async () => {
    if (!brief.purpose || !brief.target_audience) {
      toast({
        title: "Missing information",
        description: "Please fill in the funnel objective and target audience",
        variant: "destructive"
      });
      return;
    }
    
    setGenerating(true);
    setStep(3); // Move to progress step
    setUiState("generating_initial"); // MA-109: Set initial state
    setAttemptCount(1);
    setErrorGuidance("");
    
    // Progress steps for FunnelAssist generation
    const progressSteps = [
      "Analyzing your requirements...",
      "Creating qualification questions...",
      "Generating response options...",
      "Building branching structure...",
      "Creating acknowledgement messages...",
      "Adding exit nodes...",
      "Setting up contact collection...",
      "Adding consent step placeholder...",
    ];
    
    if (brief.include_booking) {
      progressSteps.push("Adding booking step...");
    }
    
    progressSteps.push("Finalizing funnel structure...");
    
    // Show progress animation
    // MA-GENERATION-LOADER-UX-REFINEMENT-146: Slowed down step timing (further refinement)
    // Previous: 600-1000ms per step (still too fast per user feedback)
    // New: 900-1400ms per step (better pacing, more deliberate)
    let currentProgress = [];
    for (const step of progressSteps) {
      currentProgress = [...currentProgress, { text: step, done: false }];
      setGenerationProgress([...currentProgress]);
      await new Promise(r => setTimeout(r, 900 + Math.random() * 500));
      currentProgress[currentProgress.length - 1].done = true;
      setGenerationProgress([...currentProgress]);
    }
    
    try {
      // Build qualification criteria from business type and traffic source
      let qualificationCriteria = brief.business_type 
        ? `Business: ${brief.business_type}. ` 
        : "";
      if (brief.traffic_source) {
        qualificationCriteria += `Traffic from ${brief.traffic_source}.`;
      }
      
      const response = await api.post("/ai-builder/generate-funnel", {
        brief: {
          purpose: brief.purpose,
          target_audience: brief.target_audience,
          qualification_criteria: qualificationCriteria || null,
          include_booking: brief.include_booking,
          tone: brief.tone,
          traffic_source: brief.traffic_source || null
        },
        funnel_name: funnelName,
        funnel_slug: funnelSlug
      });
      
      // MA-109: Handle retry metadata
      const responseData = response.data;
      const wasRetried = responseData.retried || false;
      const finalAttemptCount = responseData.attempt_count || 1;
      
      setAttemptCount(finalAttemptCount);
      setUiState("success");
      
      // Show success message
      const successMessage = wasRetried 
        ? "Your funnel is ready. We made a quick second pass to get everything just right."
        : "Your funnel is ready.";
      
      toast({
        title: "Funnel generated!",
        description: successMessage
      });
      
      // MA-110: Success state hold (Refinement 3) - 750ms (middle of 600-900ms)
      successHoldRef.current = setTimeout(() => {
        // Support both callback styles
        if (onCreated) {
          onCreated(responseData.funnel_id);
        } else if (onGenerated) {
          onGenerated(responseData);
        }
      }, 750);
      
    } catch (error) {
      // MA-109: Enhanced error handling with friendly copy
      const detail = error.response?.data?.detail;
      
      // Extract retry metadata from backend response
      const isRetryable = detail?.retryable || false;
      const wasRetried = detail?.retried || false;
      const errorAttemptCount = detail?.attempt_count || 1;
      const guidance = detail?.guidance || "";
      
      setAttemptCount(errorAttemptCount);
      setErrorGuidance(guidance);
      
      // MA-109: Map to friendly UI states
      if (!isRetryable) {
        setUiState("failed_non_retryable");
      } else if (wasRetried) {
        setUiState("failed_retryable");
      } else {
        // First attempt failed, backend will retry automatically
        // MA-110: Add 400ms delay before showing retry message (Refinement 2)
        retryDelayRef.current = setTimeout(() => {
          setUiState("retrying_once");
          setAttemptCount(2);
        }, 400); // Middle of 300-500ms range
      }
      
      // Don't show toast for retryable errors (will show in UI)
      if (!isRetryable) {
        toast({
          title: "Action needed",
          description: guidance || "Please review the information below",
          variant: "destructive"
        });
      }
      
      setGenerationProgress([]);
    } finally {
      setGenerating(false);
    }
  };
  
  // MA-109: Manual retry handler
  const handleRetry = () => {
    setUiState("generating_initial");
    setAttemptCount(1);
    setErrorGuidance("");
    handleGenerate();
  };
  
  const handleClose = () => {
    if (!generating) {
      // MA-110: Cleanup all timers
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      if (retryDelayRef.current) {
        clearTimeout(retryDelayRef.current);
        retryDelayRef.current = null;
      }
      if (successHoldRef.current) {
        clearTimeout(successHoldRef.current);
        successHoldRef.current = null;
      }
      
      setStep(1);
      setGenerationProgress([]);
      setUiState("idle");
      setErrorGuidance("");
      setAttemptCount(0);
      setProgressMessage("Structuring your flow"); // MA-111: Reset to first sub-step
      setFunnelName(externalFunnelName || "");
      setFunnelSlug(externalFunnelSlug || "");
      setBrief({
        purpose: "",
        business_type: "",
        target_audience: "",
        traffic_source: "",
        include_booking: true,
        tone: "friendly"
      });
      onClose();
    }
  };
  
  // Skip name/slug step if external values provided
  const hasExternalNameSlug = externalFunnelName && externalFunnelSlug;
  const effectiveStep = hasExternalNameSlug && step === 1 ? 2 : step;
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="glass max-w-lg border-white/10 text-slate-100">
        {effectiveStep === 1 ? (
          /* Step 1: Name/Slug Collection */
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-100">
                <Sparkles className="h-5 w-5 text-indigo-400" />
                Create with FunnelAssist
              </DialogTitle>
              <DialogDescription className="text-slate-200/60">
                Name your funnel, then describe it for AI generation
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="funnel-name" className="text-sm font-semibold">Funnel Name *</Label>
                <Input
                  id="funnel-name"
                  value={funnelName}
                  onChange={handleNameChange}
                  placeholder="e.g., Mortgage Protection Q4"
                  autoFocus
                  className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                  data-testid="ai-funnel-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="funnel-slug" className="text-sm font-semibold">URL Slug *</Label>
                <Input
                  id="funnel-slug"
                  value={funnelSlug}
                  onChange={(e) => setFunnelSlug(e.target.value)}
                  placeholder="mortgage-protection-q4"
                  className="bg-white/5 text-slate-100 ring-1 ring-white/10"
                  data-testid="ai-funnel-slug-input"
                />
                <p className="text-xs text-slate-200/60">
                  Used in the funnel URL: /funnel?funnel={funnelSlug || "your-slug"}
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleContinueToBrief}
                disabled={!funnelName || !funnelSlug}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="continue-to-brief-btn"
              >
                Continue
              </Button>
            </DialogFooter>
          </>
        ) : effectiveStep === 2 ? (
          /* Step 2: Brief Collection */
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-100">
                <Sparkles className="h-5 w-5 text-indigo-400" />
                Describe Your Funnel
              </DialogTitle>
              <DialogDescription className="text-slate-200/60">
                Tell FunnelAssist about your funnel and it will generate the complete structure
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Funnel Objective */}
              <div className="space-y-2">
                <Label htmlFor="purpose" className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-slate-200/60" />
                  Funnel Objective *
                </Label>
                <Textarea
                  id="purpose"
                  value={brief.purpose}
                  onChange={(e) => setBrief(prev => ({ ...prev, purpose: e.target.value }))}
                  placeholder="e.g., Qualify homeowners for mortgage protection insurance"
                  className="min-h-[80px]"
                  data-testid="brief-purpose"
                />
              </div>
              
              {/* Business Type / Vertical */}
              <div className="space-y-2">
                <Label htmlFor="business_type" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-200/60" />
                  Business Type / Vertical
                </Label>
                <Select
                  value={brief.business_type}
                  onValueChange={(value) => setBrief(prev => ({ ...prev, business_type: value }))}
                >
                  <SelectTrigger id="business_type" data-testid="brief-business-type">
                    <SelectValue placeholder="Select business type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mortgage_protection">Mortgage Protection</SelectItem>
                    <SelectItem value="final_expense">Final Expense</SelectItem>
                    <SelectItem value="iul">IUL / Life Insurance</SelectItem>
                    <SelectItem value="health_insurance">Health Insurance</SelectItem>
                    <SelectItem value="medicare">Medicare</SelectItem>
                    <SelectItem value="p_and_c">Property & Casualty</SelectItem>
                    <SelectItem value="financial_services">Financial Services</SelectItem>
                    <SelectItem value="real_estate">Real Estate</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Target Audience */}
              <div className="space-y-2">
                <Label htmlFor="audience" className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-200/60" />
                  Target Audience *
                </Label>
                <Input
                  id="audience"
                  value={brief.target_audience}
                  onChange={(e) => setBrief(prev => ({ ...prev, target_audience: e.target.value }))}
                  placeholder="e.g., Homeowners aged 30-65 with existing mortgages"
                  data-testid="brief-audience"
                />
              </div>
              
              {/* Traffic Source */}
              <div className="space-y-2">
                <Label htmlFor="traffic_source" className="flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-slate-200/60" />
                  Traffic Source
                  <span className="text-xs text-slate-200/60">(recommended)</span>
                </Label>
                <Select
                  value={brief.traffic_source}
                  onValueChange={(value) => setBrief(prev => ({ ...prev, traffic_source: value }))}
                >
                  <SelectTrigger id="traffic_source" data-testid="brief-traffic-source">
                    <SelectValue placeholder="Select traffic source..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="facebook_ads">Facebook Ads</SelectItem>
                    <SelectItem value="google_ads">Google Ads</SelectItem>
                    <SelectItem value="tiktok_ads">TikTok Ads</SelectItem>
                    <SelectItem value="youtube_ads">YouTube Ads</SelectItem>
                    <SelectItem value="organic_social">Organic Social</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="email">Email Campaign</SelectItem>
                    <SelectItem value="sms">SMS Campaign</SelectItem>
                    <SelectItem value="website">Website / Organic</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Include Booking Step */}
              <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-slate-200/60" />
                  <div>
                    <p className="text-sm text-slate-100">Include booking step?</p>
                    <p className="text-xs text-slate-200/60">Add appointment scheduling</p>
                  </div>
                </div>
                <Switch
                  checked={brief.include_booking}
                  onCheckedChange={(checked) => setBrief(prev => ({ ...prev, include_booking: checked }))}
                  data-testid="brief-booking-toggle"
                />
              </div>
              
              {/* Consent Warning */}
              <Alert className="bg-amber-500/10 border-amber-500/30">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <AlertDescription className="text-amber-200 text-xs">
                  FunnelAssist will include a consent step placeholder. Consent/TCPA language must be entered manually.
                </AlertDescription>
              </Alert>
            </div>
            
            <DialogFooter>
              {!hasExternalNameSlug && (
                <Button variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
              )}
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleGenerate}
                disabled={!brief.purpose || !brief.target_audience}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="generate-funnel-btn"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Funnel
              </Button>
            </DialogFooter>
          </>
        ) : (
          /* Step 3: Generation Progress - MA-109: Enhanced UX States */
          <div className="transition-opacity duration-200 ease-in-out">
            {/* Generating Initial */}
            {uiState === "generating_initial" && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 text-indigo-500 animate-spin" />
                    Generating your funnel...
                  </DialogTitle>
                  {/* MA-GENERATION-LOADER-UX-REFINEMENT-146: Removed redundant dynamic subtext */}
                  {/* Subtext competed with checklist below - cleaner without it */}
                </DialogHeader>
                
                <div className="py-6 space-y-2">
                  {generationProgress.map((item, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 text-sm"
                    >
                      {item.done ? (
                        <span className="text-green-400">✓</span>
                      ) : (
                        <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
                      )}
                      <span className={item.done ? "text-slate-200/70" : "text-slate-100"}>
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {/* Retrying Once */}
            {uiState === "retrying_once" && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 text-amber-400 animate-spin" />
                    We ran into a small issue generating your funnel—trying again now...
                  </DialogTitle>
                </DialogHeader>
                
                <div className="py-6 flex justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                </div>
              </>
            )}
            
            {/* Success */}
            {uiState === "success" && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-green-400">
                    <Check className="h-5 w-5" />
                    Your funnel is ready.
                  </DialogTitle>
                  {attemptCount > 1 && (
                    <DialogDescription className="text-slate-200/60">
                      We made a quick second pass to get everything just right.
                    </DialogDescription>
                  )}
                </DialogHeader>
                
                <div className="py-6 flex justify-center">
                  <Check className="h-12 w-12 text-green-400" />
                </div>
              </>
            )}
            
            {/* Failed Non-Retryable */}
            {uiState === "failed_non_retryable" && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-amber-400">
                    <AlertTriangle className="h-5 w-5" />
                    We need a bit more information before we can generate your funnel.
                  </DialogTitle>
                  {errorGuidance && (
                    <DialogDescription className="text-slate-200">
                      {errorGuidance}
                    </DialogDescription>
                  )}
                </DialogHeader>
                
                <div className="py-4">
                  <Alert className="bg-amber-500/10 border-amber-500/30">
                    <AlertTriangle className="h-4 w-4 text-amber-400" />
                    <AlertDescription className="text-amber-200 text-sm">
                      {errorGuidance || "Please go back and update your funnel description with more details."}
                    </AlertDescription>
                  </Alert>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={handleClose}>
                    Close
                  </Button>
                  <Button 
                    onClick={() => setStep(2)}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    Go Back
                  </Button>
                </DialogFooter>
              </>
            )}
            
            {/* Failed Retryable */}
            {uiState === "failed_retryable" && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-slate-200">
                    <AlertTriangle className="h-5 w-5 text-slate-400" />
                    We couldn't generate your funnel this time.
                  </DialogTitle>
                  <DialogDescription>
                    You can try again.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="py-4">
                  <Alert className="bg-slate-500/10 border-slate-500/30">
                    <Info className="h-4 w-4 text-slate-400" />
                    <AlertDescription className="text-slate-200 text-sm">
                      {errorGuidance || "This appears to be a temporary issue. Please try generating again."}
                    </AlertDescription>
                  </Alert>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Go Back
                  </Button>
                  <Button 
                    onClick={handleRetry}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Try Again
                  </Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
